/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the function used to create the sae reducer.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */


/*
 * Used to create a context.
 */
import * as React from 'react';

/*
 * Used to type the state of a request. 
 */
import { IRequestState, RequestState } from '@ngt/request-utilities';

/*
 * Used to create the reducer and associated actions.
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/*
 * Used to create side effects for the reducer.
 */
import { createLogic } from 'redux-logic';

/*
 * Used to type the reducer registry used to register reducers to the store.
 */

/*
 * Used to create a typed selector hook.
 */
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to get access to backend types.
 */
import * as Dtos from '../../../api/Neon.Dtos';

/*
 * Used to get access to the API types and requests
 */

import { BoundActionCreator, bindActionCreatorsWithType } from '@ngt/online-patient-management-ui';
import { OmitFirstTwoArgs } from '@ngt/online-patient-management-ui/dist/utilities/OmitArgs';
import client from '../../../api/client';
import useAsyncFunction from '../../../hooks/useAsyncFunction';
import { TypedFunction } from '../../../utilities/functionTypes';
import reducerRegistry from '../../reducerRegistry';

/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

export interface IIndividualSaeState {
    sae: Dtos.Sae<Dtos.SaeForm, Dtos.Patient> | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
    actionState: IRequestState<Dtos.ResponseStatus>
}

export interface ISaeState {
    byId: Record<string, IIndividualSaeState>;
}

export interface ISaeStore {
    sae: ISaeState;
}

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const initialIndividualSaeState: IIndividualSaeState = {
    sae: null,
    loadState: {
        state: RequestState.None
    },
    actionState: {
        state: RequestState.None
    }
};

const initialSaeState: ISaeState = {
    byId: {}
}

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class SaeReducer extends ImmerReducer<ISaeState> {
    public loadById(patientStudyNumber: string, saeNumber: number) {
        const id = `${patientStudyNumber}-${saeNumber}`;

        if (!this.draftState.byId[id]) {
            this.draftState.byId[id] = { ...initialIndividualSaeState };
        }

        this.draftState.byId[id].loadState = {
            state: RequestState.Pending
        }
    }

    public loadByIdSuccess(patientStudyNumber: string, saeNumber: number, sae?: Dtos.Sae<Dtos.SaeForm, Dtos.Patient>) {
        const id = `${patientStudyNumber}-${saeNumber}`;

        if (!this.draftState.byId[id]) {
            this.draftState.byId[id] = { ...initialIndividualSaeState };
        }

        this.draftState.byId[id].sae = sae ? sae : null;

        this.draftState.byId[id].loadState = {
            state: RequestState.Success
        };
    }

    public loadByIdFailure(patientStudyNumber: string, saeNumber: number, responseStatus: Dtos.ResponseStatus) {
        const id = `${patientStudyNumber}-${saeNumber}`;

        if (!this.draftState.byId[id]) {
            this.draftState.byId[id] = { ...initialIndividualSaeState };
        }

        this.draftState.byId[id].loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public performAction(patientStudyNumber: string, saeNumber: number, webAction: Dtos.WebAction) {
        const id = `${patientStudyNumber}-${saeNumber}`;

        if (!this.draftState.byId[id]) {
            this.draftState.byId[id] = { ...initialIndividualSaeState };
        }

        this.draftState.byId[id].actionState = {
            state: RequestState.Pending
        }
    }

    public performActionSuccess(patientStudyNumber: string, saeNumber: number) {
        const id = `${patientStudyNumber}-${saeNumber}`;

        if (!this.draftState.byId[id]) {
            this.draftState.byId[id] = { ...initialIndividualSaeState };
        }

        this.draftState.byId[id].actionState = {
            state: RequestState.Success
        };
    }

    public performActionFailure(patientStudyNumber: string, saeNumber: number, responseStatus: Dtos.ResponseStatus) {
        const id = `${patientStudyNumber}-${saeNumber}`;

        if (!this.draftState.byId[id]) {
            this.draftState.byId[id] = { ...initialIndividualSaeState };
        }

        this.draftState.byId[id].actionState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public notify(patientStudyNumber: string, saeNumber: number, notificationForm: Dtos.SaeNotificationForm) {
        const id = `${patientStudyNumber}-${saeNumber}`;

        if (!this.draftState.byId[id]) {
            this.draftState.byId[id] = { ...initialIndividualSaeState };
        }

        this.draftState.byId[id].actionState = {
            state: RequestState.Pending
        }
    }

    public notifySuccess(patientStudyNumber: string, saeNumber: number) {
        const id = `${patientStudyNumber}-${saeNumber}`;

        if (!this.draftState.byId[id]) {
            this.draftState.byId[id] = { ...initialIndividualSaeState };
        }

        this.draftState.byId[id].actionState = {
            state: RequestState.Success
        };
    }

    public notifyFailure(patientStudyNumber: string, saeNumber: number, responseStatus: Dtos.ResponseStatus) {
        const id = `${patientStudyNumber}-${saeNumber}`;

        if (!this.draftState.byId[id]) {
            this.draftState.byId[id] = { ...initialIndividualSaeState };
        }

        this.draftState.byId[id].actionState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public rejectAction(patientStudyNumber: string, saeNumber: number, rejectForm: { followUpNumber: number, reason: string, rejectedAs: number }) {
        const id = `${patientStudyNumber}-${saeNumber}`;

        if (!this.draftState.byId[id]) {
            this.draftState.byId[id] = { ...initialIndividualSaeState };
        }

        this.draftState.byId[id].actionState = {
            state: RequestState.Pending
        }
    }

    public rejectActionSuccess(patientStudyNumber: string, saeNumber: number) {
        const id = `${patientStudyNumber}-${saeNumber}`;

        if (!this.draftState.byId[id]) {
            this.draftState.byId[id] = { ...initialIndividualSaeState };
        }

        this.draftState.byId[id].actionState = {
            state: RequestState.Success
        };
    }

    public rejectActionFailure(patientStudyNumber: string, saeNumber: number, responseStatus: Dtos.ResponseStatus) {
        const id = `${patientStudyNumber}-${saeNumber}`;

        if (!this.draftState.byId[id]) {
            this.draftState.byId[id] = { ...initialIndividualSaeState };
        }

        this.draftState.byId[id].actionState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public submitInvalidForm(patientStudyNumber: string, saeNumber: number, invalidForm: Dtos.InvalidSaeForm) {
        const context = `${patientStudyNumber}-${saeNumber}`;
        if (!this.draftState.byId[context]) {
            this.draftState.byId[context] = { ...initialIndividualSaeState };
        }

        this.draftState.byId[context].actionState = {
            state: RequestState.Pending
        }
    }

    public submitInvalidFormSuccess(patientStudyNumber: string, saeNumber: number, invalidSaeForm: Dtos.InvalidSaeForm) {
        const context = `${patientStudyNumber}-${saeNumber}`;

        if (!this.draftState.byId[context]) {
            this.draftState.byId[context] = { ...initialIndividualSaeState };
        }

        this.draftState.byId[context].actionState = {
            state: RequestState.Success
        };

        if (this.draftState.byId[context].sae != null) {
            this.draftState.byId[context].sae!.invalidForm = invalidSaeForm;
        }
    }

    public submitInvalidFormFailure(patientStudyNumber: string, saeNumber: number, responseStatus: Dtos.ResponseStatus) {
        const context = `${patientStudyNumber}-${saeNumber}`;

        if (!this.draftState.byId[context]) {
            this.draftState.byId[context] = { ...initialIndividualSaeState };
        }

        this.draftState.byId[context].actionState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clearById(patientStudyNumber: string, saeNumber: number) {
        const id = `${patientStudyNumber}-${saeNumber}`;

        if (this.draftState.byId[id]) {
            delete this.draftState.byId[id];
        }
    }

    public clearAll() {
        this.draftState = { ...initialSaeState };
    }
}

export const saeActions = createActionCreators(SaeReducer);
const saeReducer = createReducerFunction(SaeReducer, initialSaeState);

export const saeApi = {
    loadById: (patientStudyNumber: string, saeNumber: number) => {
        const request: Dtos.SaeGetSingleByPatientStudyNumber = new Dtos.SaeGetSingleByPatientStudyNumber();
        request.patientStudyNumber = patientStudyNumber;
        request.saeNumber = saeNumber;
        return client.get(request);
    },
    performAction: (patientStudyNumber: string, saeNumber: number, webAction: Dtos.WebAction) => {
        const request: Dtos.SaeWebActionSave = new Dtos.SaeWebActionSave();
        request.webAction = webAction;
        return client.post(request);
    },
    notify: (patientStudyNumber: string, saeNumber: number, notificationForm: Dtos.SaeNotificationForm) => {
        const request: Dtos.SaeSendNotification = new Dtos.SaeSendNotification();
        request.patientStudyNumber = patientStudyNumber;
        request.saeNumber = saeNumber;
        request.notificationForm = notificationForm;
        return client.post(request);
    },
    rejectAction: (patientStudyNumber: string, saeNumber: number, followUpNumber: number, reason: string, rejectedAs?: number) => {
        const request: Dtos.SaeWebActionSave = new Dtos.SaeWebActionSave();
        request.webAction = new Dtos.WebAction({
            patientStudyNumber,
            saeNumber,
            saeFollowUpNumber: followUpNumber,
            actionType: Dtos.SaeWebActionTypeEnum.Rejected,
            notes: reason,
            recipients: '',
            rejectedAs: rejectedAs
        });
        return client.post(request);
    },
    submitInvalidForm: (patientStudyNumber: string, saeNumber: number, invalidForm: Dtos.InvalidSaeForm) => {
        const request = new Dtos.SaveInvalidSaeForm({
            invalidSaeForm: invalidForm
        })

        return client.post(request);
    }
}

export const saeLogics = {
    loadById: createLogic<ISaeStore, {}, undefined, string, ReturnType<typeof saeActions.loadById>>({
        type: saeActions.loadById.type,
        process: async ({ action }, dispatch, done) => {
            const [patientStudyNumber, saeNumber] = action.payload

            try {
                const response = await saeApi.loadById(patientStudyNumber, saeNumber);

                dispatch(saeActions.loadByIdSuccess(
                    patientStudyNumber,
                    saeNumber,
                    response.sae
                ));
            }
            catch (error: any) {
                dispatch(saeActions.loadByIdFailure(
                    patientStudyNumber,
                    saeNumber,
                    error ? error.responseStatus : undefined
                ));
            }

            done();
        }
    }),
    performAction: createLogic<ISaeStore, {}, undefined, string, ReturnType<typeof saeActions.performAction>>({
        type: saeActions.performAction.type,
        process: async ({ action }, dispatch, done) => {
            const [patientStudyNumber, saeNumber, webAction] = action.payload

            try {
                await saeApi.performAction(patientStudyNumber, saeNumber, webAction);

                dispatch(saeActions.performActionSuccess(
                    patientStudyNumber,
                    saeNumber
                ));
            }
            catch (error: any) {
                dispatch(saeActions.performActionFailure(
                    patientStudyNumber,
                    saeNumber,
                    error ? error.responseStatus : undefined
                ));
            }

            done();
        }
    }),
    notify: createLogic<ISaeStore, {}, undefined, string, ReturnType<typeof saeActions.notify>>({
        type: saeActions.notify.type,
        process: async ({ action }, dispatch, done) => {
            const [patientStudyNumber, saeNumber, notificationForm] = action.payload

            try {
                await saeApi.notify(patientStudyNumber, saeNumber, notificationForm);

                dispatch(saeActions.notifySuccess(
                    patientStudyNumber,
                    saeNumber
                ));
            }
            catch (error: any) {
                dispatch(saeActions.notifyFailure(
                    patientStudyNumber,
                    saeNumber,
                    error ? error.responseStatus : undefined
                ));
            }

            done();
        }
    }),
    rejectAction: createLogic<ISaeStore, {}, undefined, string, ReturnType<typeof saeActions.rejectAction>>({
        type: saeActions.rejectAction.type,
        process: async ({ action }, dispatch, done) => {
            const [patientStudyNumber, saeNumber, rejectForm] = action.payload

            try {
                await saeApi.rejectAction(patientStudyNumber, saeNumber, rejectForm.followUpNumber, rejectForm.reason, rejectForm.rejectedAs);

                dispatch(saeActions.rejectActionSuccess(
                    patientStudyNumber,
                    saeNumber
                ));
            }
            catch (error: any) {
                dispatch(saeActions.rejectActionFailure(
                    patientStudyNumber,
                    saeNumber,
                    error ? error.responseStatus : undefined
                ));
            }

            done();
        }
    }),
    submitInvalidForm: createLogic<ISaeStore, {}, undefined, string, ReturnType<typeof saeActions.submitInvalidForm>>({
        type: saeActions.submitInvalidForm.type,
        process: async ({ action }, dispatch, done) => {
            const [patientStudyNumber, saeNumber, invalidForm] = action.payload

            try {
                const response = await saeApi.submitInvalidForm(patientStudyNumber, saeNumber, invalidForm);

                dispatch(saeActions.submitInvalidFormSuccess(
                    patientStudyNumber,
                    saeNumber,
                    response.invalidSaeForm
                ));
            }
            catch (error: any) {
                dispatch(saeActions.submitInvalidFormFailure(
                    patientStudyNumber,
                    saeNumber,
                    error ? error.responseStatus : undefined
                ));
            }

            done();
        }
    })
}

const logics = [
    saeLogics.loadById,
    saeLogics.performAction,
    saeLogics.notify,
    saeLogics.rejectAction,
    saeLogics.submitInvalidForm
];

export const useSaeSelector: TypedUseSelectorHook<ISaeStore> = useSelector;

export const saeSelectors = {
    saeById: (state: ISaeStore, id: string) => state.sae?.byId[id]?.sae ?? initialIndividualSaeState.sae,
    loadStateById: (state: ISaeStore, id: string) => state.sae?.byId[id]?.loadState ?? initialIndividualSaeState.loadState,
    actionStateById: (state: ISaeStore, id: string) => state.sae?.byId[id]?.actionState ?? initialIndividualSaeState.actionState
}

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export interface IUseSaeByIdActions {
    load: BoundActionCreator<OmitFirstTwoArgs<typeof saeActions.loadById>>;
    clear: BoundActionCreator<OmitFirstTwoArgs<typeof saeActions.clearById>>;
    performAction: BoundActionCreator<OmitFirstTwoArgs<typeof saeActions.performAction>>;
    notify: BoundActionCreator<OmitFirstTwoArgs<typeof saeActions.notify>>;
    notifySuccess: BoundActionCreator<OmitFirstTwoArgs<typeof saeActions.notifySuccess>>;
    notifyFailure: BoundActionCreator<OmitFirstTwoArgs<typeof saeActions.notifyFailure>>;
    rejectAction: BoundActionCreator<OmitFirstTwoArgs<typeof saeActions.rejectAction>>;
    rejectActionSuccess: BoundActionCreator<OmitFirstTwoArgs<typeof saeActions.rejectActionSuccess>>;
    rejectActionFailure: BoundActionCreator<OmitFirstTwoArgs<typeof saeActions.rejectActionFailure>>;
    submitInvalidForm: BoundActionCreator<OmitFirstTwoArgs<typeof saeActions.submitInvalidForm>>;
    asyncNotify: TypedFunction<Parameters<BoundActionCreator<OmitFirstTwoArgs<typeof saeActions.notify>>>, Promise<Dtos.SaeNotificationForm>>
    asyncReject: TypedFunction<Parameters<BoundActionCreator<OmitFirstTwoArgs<typeof saeActions.rejectAction>>>, Promise<{ followUpNumber: number, reason: string, rejectedAs: number }>>
    asyncSubmitInvalidSaeForm: TypedFunction<Parameters<BoundActionCreator<OmitFirstTwoArgs<typeof saeActions.submitInvalidForm>>>, Promise<Dtos.InvalidSaeForm>>
}

export const useSaeById = (patientStudyNumber: string, saeNumber: number): [
    Dtos.Sae<Dtos.SaeForm, Dtos.Patient> | null,
    IRequestState<Dtos.ResponseStatus>,
    IUseSaeByIdActions,
    IRequestState<Dtos.ResponseStatus>,
] => {
    const dispatch = useDispatch();

    const unboundAsyncNotify = useAsyncFunction(saeActions.notify, saeActions.notifySuccess, saeActions.notifyFailure);

    const unboundAsyncReject = useAsyncFunction(saeActions.rejectAction, saeActions.rejectActionSuccess, saeActions.rejectActionSuccess);

    const unboundAsyncSubmitInvalidSaeForm = useAsyncFunction(saeActions.submitInvalidForm, saeActions.submitInvalidFormSuccess, saeActions.submitInvalidFormFailure);

    //const unboundAsyncReview = useAsyncFunction(saeActions.reviewAction, saeActions.reviewActionSuccess, saeActions.reviewActionSuccess);

    const actions = React.useMemo(() => {
        const load = () => saeActions.loadById(patientStudyNumber, saeNumber);
        load.type = saeActions.loadById.type;

        const clear = () => saeActions.clearById(patientStudyNumber, saeNumber);
        clear.type = saeActions.clearById.type;

        const performAction = (webAction: Dtos.WebAction) => saeActions.performAction(patientStudyNumber, saeNumber, webAction);
        performAction.type = saeActions.performAction.type;

        const notify = (notificationForm: Dtos.SaeNotificationForm) => saeActions.notify(patientStudyNumber, saeNumber, notificationForm);
        notify.type = saeActions.notify.type;

        const notifySuccess = () => saeActions.notifySuccess(patientStudyNumber, saeNumber);
        notifySuccess.type = saeActions.notifySuccess.type;

        const notifyFailure = (responseStatus: Dtos.ResponseStatus) => saeActions.notifyFailure(patientStudyNumber, saeNumber, responseStatus);
        notifyFailure.type = saeActions.notifyFailure.type;

        const asyncNotify = async (form: Dtos.SaeNotificationForm) => {
            const [, ,] = await unboundAsyncNotify([patientStudyNumber, saeNumber, form]);

            return form;
        };

        const rejectAction = (rejectForm: { followUpNumber: number, reason: string, rejectedAs: number }) => saeActions.rejectAction(patientStudyNumber, saeNumber, rejectForm);
        rejectAction.type = saeActions.rejectAction.type;

        const rejectActionSuccess = () => saeActions.rejectActionSuccess(patientStudyNumber, saeNumber);
        rejectActionSuccess.type = saeActions.rejectActionSuccess.type;

        const rejectActionFailure = (responseStatus: Dtos.ResponseStatus) => saeActions.rejectActionFailure(patientStudyNumber, saeNumber, responseStatus);
        rejectActionFailure.type = saeActions.rejectActionFailure.type;

        const asyncReject = async (rejectForm: { followUpNumber: number, reason: string, rejectedAs: number }) => {
            const [, ,] = await unboundAsyncReject([patientStudyNumber, saeNumber, rejectForm]);

            return rejectForm;
        };

        const submitInvalidSaeForm = (invalidSaeForm: Dtos.InvalidSaeForm) => saeActions.submitInvalidForm(patientStudyNumber, saeNumber, invalidSaeForm);
        submitInvalidSaeForm.type = saeActions.submitInvalidForm.type;

        const asyncSubmitInvalidSaeForm = async (invalidSaeForm: Dtos.InvalidSaeForm) => {
            const [, ,] = await unboundAsyncSubmitInvalidSaeForm([patientStudyNumber, saeNumber, invalidSaeForm]);

            return invalidSaeForm;
        };

        return {
            ...bindActionCreatorsWithType<IUseSaeByIdActions>({
                load,
                clear,
                performAction,
                notify,
                notifySuccess,
                notifyFailure,
                rejectAction,
                rejectActionSuccess,
                rejectActionFailure,
                submitInvalidSaeForm
            }, dispatch),
            asyncNotify,
            asyncReject,
            asyncSubmitInvalidSaeForm
        };
    }, [patientStudyNumber, saeNumber, dispatch, unboundAsyncNotify, unboundAsyncReject, unboundAsyncSubmitInvalidSaeForm]);

    const saeSelector = React.useCallback((state: ISaeStore) => {
        const id = `${patientStudyNumber}-${saeNumber}`;

        return saeSelectors.saeById(state, id)
    }, [patientStudyNumber, saeNumber]);

    const loadStateSelector = React.useCallback((state: ISaeStore) => {
        const id = `${patientStudyNumber}-${saeNumber}`;

        return saeSelectors.loadStateById(state, id)
    }, [patientStudyNumber, saeNumber]);

    const actionStateSelector = React.useCallback((state: ISaeStore) => {
        const id = `${patientStudyNumber}-${saeNumber}`;

        return saeSelectors.actionStateById(state, id)
    }, [patientStudyNumber, saeNumber]);

    const sae = useSaeSelector(saeSelector);

    const loadState = useSaeSelector(loadStateSelector);

    const actionState = useSaeSelector(actionStateSelector);

    return [
        sae,
        loadState,
        actions,
        actionState
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('sae', saeReducer as any, logics as any);


